import { useCallback, useEffect, useRef, useState } from "react";
import CONFIG from "config/config";
import { authorization } from "module/auth";
import { meRequestInit, meUrl } from "module/api/me";

/**
 * The Campaign Builder billing-profile gate. A single derived state — is the
 * acting team's org one that requires a profile, does it already hold one, and
 * may this viewer set one up — driving the banner and the payment block across
 * every builder surface. The server enforces the same rule; the disabled button
 * is never the enforcement.
 */

export type GateState = "none" | "informational" | "required";

export interface BillingProfileGate {
  loading: boolean;
  state: GateState;
  /** Whether this viewer may set a profile up (Org Admin, or Team Admin of a single-team org). */
  canSetup: boolean;
  refresh: () => void;
}

interface MeOrganization {
  orgRole?: string | null;
  orgType?: string | null;
}
interface MeTeam {
  id: string | number;
  teamRole?: string | null;
}
interface MeResponse {
  organization?: MeOrganization | null;
  teams?: MeTeam[] | null;
}

// Single-team org types where a Team Admin may act; everything else (and any
// unrecognised type — fail closed) is treated as multi-team, matching the
// server's orgTypeAllowsMultipleTeams.
const SINGLE_TEAM_ORG_TYPES = ["STANDARD", "EDDM_LITE"];
const isSingleTeamOrg = (orgType: string | null | undefined): boolean =>
  !!orgType && SINGLE_TEAM_ORG_TYPES.includes(orgType);

// /me is a per-context snapshot (same dedupe idea as UserMenu's fetchLiveUser).
let liveMeRequest: Promise<MeResponse | null> | null = null;
function fetchMe(): Promise<MeResponse | null> {
  if (liveMeRequest) return liveMeRequest;
  const apiKey = authorization.getAPIKey();
  if (!apiKey) return Promise.resolve(null);
  // x-api-key, no cookies: an ambient session cookie must not decide whose org gates checkout (PAD-33097).
  liveMeRequest = fetch(meUrl(), meRequestInit(apiKey))
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      const me = body ? body.user ?? body : null;
      // A failed snapshot is not "no account": drop it so the next caller retries.
      if (me === null) liveMeRequest = null;
      return me;
    })
    .catch(() => {
      liveMeRequest = null;
      return null;
    });
  return liveMeRequest;
}

// Master Account = the acting user belongs to an organization. It switches
// checkout from the legacy per-user card endpoints to the team-scoped ones.
// Fails closed: an unreadable /me must never route a Master Account team onto
// the legacy per-user vault, so the caller surfaces the failure instead.
export async function fetchIsMasterAccount(): Promise<boolean> {
  const me = await fetchMe();
  if (me === null) throw new Error("Could not load your account. Please try again.");
  return !!me.organization;
}

// A profile created on one surface must clear the gate on every other mounted
// surface (builder step, order-details drawer, payment drawer), which each hold
// their own hook instance. refresh() fans out to all of them so none keeps
// showing a stale "profile required" banner.
const gateRefreshListeners = new Set<() => void>();
function notifyGateRefresh(): void {
  gateRefreshListeners.forEach((listener) => listener());
}

async function fetchTeamHasProfile(clientId: string): Promise<boolean> {
  const apiKey = authorization.getAPIKey();
  if (!apiKey) return false;
  const response = await fetch(`${CONFIG.API_URL}/billing/teams/${clientId}/profile`, {
    headers: { Accept: "application/json", "x-api-key": apiKey },
  });
  if (!response.ok) return false;
  const body = await response.json().catch(() => null);
  return !!body?.profile;
}

export function useBillingProfileGate(clientId: string | undefined): BillingProfileGate {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<GateState>("none");
  const [canSetup, setCanSetup] = useState(false);
  const [nonce, setNonce] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!clientId) {
      setLoading(false);
      setState("none");
      return;
    }
    setLoading(true);
    Promise.all([fetchMe(), fetchTeamHasProfile(clientId)])
      .then(([me, hasProfile]) => {
        if (cancelled.current) return;
        const org = me?.organization ?? null;
        // No organization = a legacy, non-Master-Account client: no gate.
        if (!org) {
          setState("none");
          setCanSetup(false);
          return;
        }
        const orgType = org.orgType ?? null;
        const orgRole = org.orgRole ?? null;
        const teamRole = me?.teams?.find((t) => String(t.id) === String(clientId))?.teamRole ?? null;
        // Org Admin anywhere, or a Team Admin of a single-team org, may set up.
        setCanSetup(orgRole === "ORG_ADMIN" || (teamRole === "TEAM_ADMIN" && isSingleTeamOrg(orgType)));

        if (hasProfile) {
          setState("none");
        } else if (isSingleTeamOrg(orgType)) {
          setState("informational");
        } else {
          setState("required");
        }
      })
      .finally(() => {
        if (!cancelled.current) setLoading(false);
      });
    return () => {
      cancelled.current = true;
    };
  }, [clientId, nonce]);

  useEffect(() => {
    const onRefresh = () => setNonce((n) => n + 1);
    gateRefreshListeners.add(onRefresh);
    return () => {
      gateRefreshListeners.delete(onRefresh);
    };
  }, []);

  const refresh = useCallback(() => {
    // A profile was just created — re-read the team profile (org context is
    // stable for the session) on every mounted gate instance, not just this one.
    notifyGateRefresh();
  }, []);

  return { loading, state, canSetup, refresh };
}
